import SectionPanel from '@/components/SectionPanel';
import { Flex, SegmentGroup, Stack, Text, useDisclosure } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { de } from "date-fns/locale";
import MonthNavigation from "./MonthNavigation";
import { formatIso8601 } from "@/data/DateTimeUtil";
import TimesheetDialog from "../TimesheetDialog";
import { Holiday } from "@/api/Holiday";
import { Timesheet as TimesheetEntry } from "@/api/Timesheet";
import IdAndNameData from "@/data/IdAndNameData";
import ConfirmDialog from "@/components/ConfirmDialog";
import WaitingSpinner from "@/components/WaitingSpinner";
import { AddIcon } from "@/components/icons/Icon";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import ContractTabs from "@/components/ContractTabs";
import { Absence } from "@/api/Absence";
import { ContractSummary } from "@/api/ContractSummary";
import { absenceService } from "@/service/AbsenceService";
import { holidayService } from "@/service/HolidayService";
import { usePageRefresh } from "@/hooks/usePageRefresh";
import { useProjectNames } from "@/hooks/useProjectNames";
import { timesheetService } from "@/service/TimesheetService";
import { userService } from "@/service/UserService";
import { workmodelService } from "@/service/WorkModelService";
import { TimesheetCalendarGrid } from "./TimesheetCalendarGrid";
import { ApprovedInhabitant } from "@/api/Approved";
import { PendingInhabitant } from "@/api/Pending";
import { RejectedInhabitant } from "@/api/Rejected";
import { OrgUtil } from "@/data/OrgUtil";
import { ApprovalStatusUtil } from "@/data/ApprovalStatusUtil";
import { useAuthContext, useOrganization } from "@/auth/AuthContext";
import { summarizeTimesheetPendingMinutes } from "@/data/ApprovalSummaryUtil";
import {
  buildCalendarWeeks,
  buildWeekdays,
  createDraftEntry,
  DEFAULT_ENTRY_MINUTES,
  getCalendarRange,
  getEntriesForDate,
  groupEntriesByDate,
  mapHolidaysByDate,
  proposeEntryTimes,
  sumMonthMinutes,
  TimesheetView,
} from "./TimesheetCalendarUtils";

type TimesheetCalendarProps = {
  userId?: number;
  organizationId?: number;
  initialMonth?: Date;
  active?: boolean;
};

type TimesheetPreferences = {
  view: TimesheetView;
  showWeekends: boolean;
};

const TIMESHEET_PREFERENCES_KEY = "cooperaid.timesheet.preferences";

const isTimesheetView = (value: unknown): value is TimesheetView =>
  value === "day" || value === "week" || value === "month";

const readTimesheetPreferences = (userId?: number): TimesheetPreferences => {
  const defaults: TimesheetPreferences = { view: "week", showWeekends: false };

  try {
    const stored = localStorage.getItem(`${TIMESHEET_PREFERENCES_KEY}.${userId ?? "anonymous"}`);
    if (!stored) {
      return defaults;
    }

    const parsed = JSON.parse(stored) as Partial<TimesheetPreferences>;
    return {
      view: isTimesheetView(parsed.view) ? parsed.view : defaults.view,
      showWeekends: typeof parsed.showWeekends === "boolean" ? parsed.showWeekends : defaults.showWeekends,
    };
  } catch {
    return defaults;
  }
};

const TimesheetCalendar = ({ userId, organizationId, initialMonth, active = true }: TimesheetCalendarProps) => {
  const revision = usePageRefresh();
  const { user } = useAuthContext();
  const organization = useOrganization();
  const token = user?.token?.trim();
  const loadedOrganizationId = organization?.id;
  const country = organization?.country?.trim().toUpperCase() || "CH";
  const city = organization?.city?.trim() || undefined;
  const isCalendarContextReady = !!token && !!organization && loadedOrganizationId === organizationId;
  const preferenceUserId = user?.data?.id;
  const [deleteData, setDeleteData] = useState<IdAndNameData<number> | null>(null);
  const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [editData, setEditData] = useState<TimesheetEntry>(() =>
    createDraftEntry({ day: new Date(), userId, organizationId })
  );
  const { open: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(initialMonth ?? new Date());
  const [view, setView] = useState<TimesheetView>(initialMonth ? "month" : "week");
  const [showWeekends, setShowWeekends] = useState(false);
  const [preferencesLoadedForUserId, setPreferencesLoadedForUserId] = useState<number | undefined>();
  const [timesheetEntries, setTimesheetEntries] = useState<TimesheetEntry[]>([]);
  const [holidayMap, setHolidayMap] = useState<Map<string, Holiday>>(new Map());
  const [absenceMap, setAbsenceMap] = useState<Map<string, Absence[]>>(new Map());
  const { options: projectNames, nameById: projects } = useProjectNames();
  const [contract, setContract] = useState<ContractSummary | null>(null);
  const [reportsByManager, setReportsByManager] = useState<Map<number, number[]>>(new Map());

  const calendarRange = useMemo(() => getCalendarRange(currentMonth, view), [currentMonth, view]);
  const monthRange = useMemo(() => getCalendarRange(currentMonth), [currentMonth]);
  const weekdays = useMemo(() => buildWeekdays(currentMonth, view), [currentMonth, view]);
  const calendarWeeks = useMemo(() => buildCalendarWeeks(calendarRange, view), [calendarRange, view]);
  const entriesByDate = useMemo(() => groupEntriesByDate(timesheetEntries), [timesheetEntries]);
  const monthMinutes = useMemo(() => sumMonthMinutes(timesheetEntries, currentMonth), [timesheetEntries, currentMonth]);
  const currentUserId = user?.data?.id;
  const role = user?.data?.role;
  const isCurrentUserAdmin = role ? OrgUtil.isRoleAdmin(role) || OrgUtil.isRoleRoot(role) : false;
  const isCalendarLoading = isLoading || (!!token && !isCalendarContextReady);
  const canInteract = isCalendarContextReady && !isCalendarLoading && !error;
  const canCreateTimesheets = !!userId && !!organizationId && (userId === currentUserId || isCurrentUserAdmin);
  const canApproveTimesheets = !!userId && (
    isCurrentUserAdmin ||
    (!!currentUserId && userId !== currentUserId && (reportsByManager.get(currentUserId) ?? []).includes(userId))
  );

  const isEntryInCurrentMonth = useCallback((entry: TimesheetEntry): boolean => {
    const firstDay = formatIso8601(monthRange.monthStart);
    const lastDay = formatIso8601(monthRange.monthEnd);
    return entry.localDate >= firstDay && entry.localDate <= lastDay;
  }, [monthRange.monthEnd, monthRange.monthStart]);

  useEffect(() => {
    onEditClose();
    onDeleteClose();
    setDeleteData(null);
  }, [token, loadedOrganizationId, organizationId, userId, onEditClose, onDeleteClose]);

  useEffect(() => {
    setCurrentMonth(current => {
      const next = initialMonth ?? new Date();
      return formatIso8601(current) === formatIso8601(next) ? current : next;
    });
  }, [initialMonth, userId]);

  useEffect(() => {
    if (preferenceUserId === undefined) {
      return;
    }

    const preferences = readTimesheetPreferences(preferenceUserId);
    setView(initialMonth ? "month" : preferences.view);
    setShowWeekends(preferences.showWeekends);
    setPreferencesLoadedForUserId(preferenceUserId);
  }, [preferenceUserId, initialMonth, userId]);

  useEffect(() => {
    // Inbox context should not overwrite the user's saved calendar view.
    if (initialMonth || preferenceUserId === undefined || preferencesLoadedForUserId !== preferenceUserId) {
      return;
    }

    localStorage.setItem(
      `${TIMESHEET_PREFERENCES_KEY}.${preferenceUserId}`,
      JSON.stringify({ view, showWeekends }),
    );
  }, [initialMonth, preferenceUserId, preferencesLoadedForUserId, showWeekends, view]);

  const canEditEntry = useCallback((entry?: TimesheetEntry): boolean =>
    canInteract && !!entry &&
    (
      (ApprovalStatusUtil.isPending(entry.status) && (entry.userId === currentUserId || isCurrentUserAdmin)) ||
      (ApprovalStatusUtil.isRejected(entry.status) && entry.userId === currentUserId)
    )
  , [canInteract, currentUserId, isCurrentUserAdmin]);

  const canCancelEntry = useCallback((entry?: TimesheetEntry): boolean =>
    canInteract && !!entry &&
    entry.id > 0 &&
    !ApprovalStatusUtil.isCancelled(entry.status) &&
    (entry.userId === currentUserId || isCurrentUserAdmin)
  , [canInteract, currentUserId, isCurrentUserAdmin]);

  const canApproveEntry = useCallback((entry?: TimesheetEntry): boolean =>
    canInteract && !!entry &&
    entry.id > 0 &&
    ApprovalStatusUtil.isPending(entry.status) &&
    canApproveTimesheets
  , [canInteract, canApproveTimesheets]);

  const canRejectEntry = useCallback((entry?: TimesheetEntry): boolean =>
    canInteract && !!entry &&
    entry.id > 0 &&
    ApprovalStatusUtil.isPending(entry.status) &&
    canApproveTimesheets
  , [canInteract, canApproveTimesheets]);

  const hasPendingMonthEntries = useMemo(() =>
    timesheetEntries.some((entry) =>
      entry.userId === userId &&
      isEntryInCurrentMonth(entry) &&
      ApprovalStatusUtil.isPending(entry.status)
    )
  , [isEntryInCurrentMonth, timesheetEntries, userId]);
  const pendingMonthMinutes = useMemo(() =>
    summarizeTimesheetPendingMinutes(
      timesheetEntries.filter((entry) => entry.userId === userId && isEntryInCurrentMonth(entry))
    )
  , [isEntryInCurrentMonth, timesheetEntries, userId]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(!!token);
    setError(null);
    setTimesheetEntries([]);
    setHolidayMap(new Map());
    setAbsenceMap(new Map());
    setContract(null);
    if (!isCalendarContextReady || !active) return;

    const from = formatIso8601(calendarRange.visibleStart);
    const to = formatIso8601(calendarRange.visibleEnd);
    const timesheetPromise = userId ? timesheetService.findAll(userId, from, to) : Promise.resolve<TimesheetEntry[]>([]);
    const contractPromise = userId ? workmodelService.contractSummary(userId) : Promise.resolve<ContractSummary | null>(null);
    const absencePromise = userId ? absenceService.findAll(userId, from, to) : Promise.resolve<Map<string, Absence[]>>(new Map());

    Promise.all([
      timesheetPromise,
      holidayService.holidays(currentMonth.getFullYear(), country, "DE", city),
      contractPromise,
      absencePromise
    ])
      .then(([entries, holidays, contractSummary, absences]) => {
        if (!isActive) {
          return;
        }

        setTimesheetEntries(entries);
        setHolidayMap(mapHolidaysByDate(holidays));
        setContract(contractSummary);
        setAbsenceMap(absences);
      })
      .catch(() => {
        if (isActive) setError("Zeiterfassungskalender konnte nicht geladen werden.");
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [calendarRange, currentMonth, userId, token, loadedOrganizationId, organizationId, country, city, isCalendarContextReady, revision, active]);

  useEffect(() => {
    let isActive = true;
    setReportsByManager(new Map());
    if (!isCalendarContextReady) return;

    userService.listReports()
      .then((response) => {
        if (!isActive) {
          return;
        }

        setReportsByManager(userService.toNumberArrayMap(
          response.reports as Readonly<Map<number, ReadonlyArray<number>>> | Record<string, number[]>
        ));
      })
      .catch(() => {
        if (isActive) {
          setReportsByManager(new Map());
        }
      });

    return () => {
      isActive = false;
    };
  }, [token, loadedOrganizationId, currentUserId, isCalendarContextReady]);

  const handleEditEntry = useCallback((entry: TimesheetEntry) => {
    setEditData(entry);
    onEditOpen();
  }, [onEditOpen]);

  const handleDeleteEntry = useCallback((data: IdAndNameData<number>) => {
    setDeleteData(data);
    onDeleteOpen();
  }, [onDeleteOpen]);

  const handleNewEntry = useCallback((day: Date, dailyEntries: TimesheetEntry[]) => {
    if (!canCreateTimesheets || !canInteract) {
      return;
    }

    const { startTime, endTime } = proposeEntryTimes(contract?.contract.minutesPerDay ?? 0, dailyEntries);

    setEditData(createDraftEntry({
      day,
      userId,
      organizationId,
      startTime,
      endTime,
      minutes: DEFAULT_ENTRY_MINUTES
    }));
    onEditOpen();
  }, [canCreateTimesheets, canInteract, contract?.contract.minutesPerDay, onEditOpen, organizationId, userId]);

  const handleNewEntryToday = useCallback(() => {
    const today = new Date();
    handleNewEntry(today, getEntriesForDate(entriesByDate, today));
  }, [entriesByDate, handleNewEntry]);

  const handleEntrySave = useCallback(async (data: TimesheetEntry): Promise<void> => {
    const newEntry = data.id < 0;
    const resubmittedEntry = !newEntry && ApprovalStatusUtil.isRejected(data.status);
    const saveEntry: Promise<number> = newEntry
      ? timesheetService.add(data)
      : resubmittedEntry
        ? timesheetService.resubmit(data)
        : timesheetService.update(data);

    const id = await saveEntry;
    const updatedEntry = resubmittedEntry
      ? {
        ...data,
        id,
        status: PendingInhabitant,
        decidedBy: null,
        decidedAt: null,
        decisionReason: null
      }
      : { ...data, id };

    setTimesheetEntries((prevEntries) => {
      if (newEntry) {
        return [...prevEntries, updatedEntry];
      }

      return prevEntries.map((entry) => entry.id === updatedEntry.id ? updatedEntry : entry);
    });
  }, []);

  const handleCancelEntry = useCallback(async (entry: TimesheetEntry): Promise<void> => {
    onEditClose();
    await timesheetService.remove(entry.userId, entry.id);
    setTimesheetEntries((prevEntries) => prevEntries.filter((existing) => existing.id !== entry.id));
  }, [onEditClose]);

  const handleApproveEntry = useCallback(async (entry: TimesheetEntry): Promise<void> => {
    onEditClose();
    await timesheetService.approve(entry.userId, entry.id);
    setTimesheetEntries((prevEntries) =>
      prevEntries.map((existing) => existing.id === entry.id ? {
        ...existing,
        status: ApprovedInhabitant,
        decidedBy: currentUserId ?? null,
        decidedAt: new Date(),
        decisionReason: null
      } : existing)
    );
  }, [currentUserId, onEditClose]);

  const handleRejectEntry = useCallback(async (entry: TimesheetEntry, reason: string): Promise<void> => {
    onEditClose();
    const trimmedReason = reason.trim();
    await timesheetService.reject(entry.userId, entry.id, trimmedReason);
    setTimesheetEntries((prevEntries) =>
      prevEntries.map((existing) => existing.id === entry.id ? {
        ...existing,
        status: RejectedInhabitant,
        decidedBy: currentUserId ?? null,
        decidedAt: new Date(),
        decisionReason: trimmedReason
      } : existing)
    );
  }, [currentUserId, onEditClose]);

  const handleApproveMonth = useCallback(async (): Promise<void> => {
    if (!userId || !canInteract || !canApproveTimesheets) {
      return;
    }

    await timesheetService.approveMonth(userId, currentMonth.getFullYear(), currentMonth.getMonth() + 1);
    setTimesheetEntries((prevEntries) =>
      prevEntries.map((entry) =>
        entry.userId === userId && isEntryInCurrentMonth(entry) && ApprovalStatusUtil.isPending(entry.status)
          ? {
            ...entry,
            status: ApprovedInhabitant,
            decidedBy: currentUserId ?? null,
            decidedAt: new Date(),
            decisionReason: null
          }
          : entry
      )
    );
  }, [currentMonth, currentUserId, isEntryInCurrentMonth, userId, canInteract, canApproveTimesheets]);

  const handleConfirmDelete = useCallback(async (): Promise<void> => {
    if (!deleteData || !userId) {
      return;
    }

    await timesheetService.remove(userId, deleteData.id);
    setTimesheetEntries((prevEntries) => prevEntries.filter((entry) => entry.id !== deleteData.id));
  }, [deleteData, userId]);

  return (
    <Stack gap='6' minW='0'>
      <ContractTabs
        summary={canInteract ? contract : null}
        month={currentMonth}
        monthMinutes={canInteract ? monthMinutes : null}
        approvalSummary={{ timesheetMinutes: canInteract ? pendingMonthMinutes : 0 }}
      />
      <SectionPanel>
        <Flex alignItems="center" gap={4} wrap="wrap">
          <MonthNavigation currentMonth={currentMonth} setCurrentMonth={setCurrentMonth} language={de} view={view} />
          <SegmentGroup.Root
            aria-label="Kalenderansicht"
            flexShrink={0}
            overflow='visible'
            value={view}
            onValueChange={({ value }) => {
              if (isTimesheetView(value)) {
                setView(value);
              }
            }}
          >
            <SegmentGroup.Indicator />
            <SegmentGroup.Items items={[
              { value: "day", label: "Tag" },
              { value: "week", label: "Woche" },
              { value: "month", label: "Monat" },
            ]} />
          </SegmentGroup.Root>
          <Switch
            id="timesheet-show-weekends"
            colorPalette="blue"
            checked={showWeekends}
            disabled={view === "day"}
            onCheckedChange={({ checked }) => setShowWeekends(checked)}
          >
            Wochenenden anzeigen
          </Switch>
          {canCreateTimesheets && (
            <Button
              variant="solid"
              colorPalette="blue"
              aria-label="Neuer Eintrag"
              disabled={!canInteract}
              onClick={handleNewEntryToday}
            >
              <AddIcon /> Neuer Eintrag
            </Button>
          )}
          {view === "month" && canApproveTimesheets && (
            <Button
              variant="solid"
              colorPalette="green"
              aria-label="Monat genehmigen"
              disabled={!canInteract || !hasPendingMonthEntries}
              onClick={handleApproveMonth}
            >
              Monat genehmigen
            </Button>
          )}
          {isCalendarLoading && <WaitingSpinner role="status" aria-label="Zeiterfassungskalender wird geladen" size="lg" />}
        </Flex>

        {error && <Text role="alert" color="red.fg">{error}</Text>}
        {canInteract && <TimesheetCalendarGrid
          view={view}
          showWeekends={showWeekends}
          weekdays={weekdays}
          weeks={calendarWeeks}
          monthStart={calendarRange.monthStart}
          entriesByDate={entriesByDate}
          holidayMap={holidayMap}
          absenceMap={absenceMap}
          projects={projects}
          onEditEntry={handleEditEntry}
          onDeleteEntry={handleDeleteEntry}
          onApproveEntry={handleApproveEntry}
          onNewEntry={handleNewEntry}
          canEditEntry={canEditEntry}
          canCancelEntry={canCancelEntry}
          canApproveEntry={canApproveEntry}
          canCreateEntry={canCreateTimesheets}
        />}
      </SectionPanel>

      <TimesheetDialog
        projectNames={projectNames}
        isOpen={isEditOpen && canInteract}
        onClose={onEditClose}
        entry={editData}
        onSave={handleEntrySave}
        onDelete={handleCancelEntry}
        onApprove={handleApproveEntry}
        onReject={handleRejectEntry}
        canEdit={canEditEntry(editData)}
        canCancel={canCancelEntry(editData)}
        canApprove={canApproveEntry(editData)}
        canReject={canRejectEntry(editData)}
      />
      <ConfirmDialog
        heading={deleteData ? `${deleteData.name} stornieren` : "Eintrag stornieren"}
        isOpen={isDeleteOpen && canInteract}
        onClose={onDeleteClose}
        onConfirm={handleConfirmDelete}
      />
    </Stack>
  );
};

export default TimesheetCalendar;
